import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Before/after drag slider for shot cards (HDR/Original vs AI).
 * Drag the handle to compare; click a half to open that image.
 */
export default function CompactImageCompareSlider({
  beforeUrl,
  afterUrl,
  beforeLabel = "ORIGINAL",
  afterLabel = "AI",
  afterFilter = "",
  onImageClick,
  className = "",
}) {
  const [position, setPosition] = useState(50);
  const [dragging, setDragging] = useState(false);
  const [frameWidth, setFrameWidth] = useState(0);
  const containerRef = useRef(null);
  const handleRef = useRef(null);
  const didDragRef = useRef(false);
  const pointerStartXRef = useRef(null);

  const updatePosition = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setPosition((x / rect.width) * 100);
  }, []);

  useEffect(() => {
    setPosition(50);
  }, [beforeUrl, afterUrl]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => setFrameWidth(el.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [beforeUrl, afterUrl]);

  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => {
      e.preventDefault();
      if (
        pointerStartXRef.current != null &&
        Math.abs(e.clientX - pointerStartXRef.current) > 4
      ) {
        didDragRef.current = true;
      }
      updatePosition(e.clientX);
    };
    const onTouchMove = (e) => {
      e.preventDefault();
      if (!e.touches?.[0]) return;
      const x = e.touches[0].clientX;
      if (
        pointerStartXRef.current != null &&
        Math.abs(x - pointerStartXRef.current) > 4
      ) {
        didDragRef.current = true;
      }
      updatePosition(x);
    };
    const onUp = () => {
      setDragging(false);
      pointerStartXRef.current = null;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onUp);
    };
  }, [dragging, updatePosition]);

  if (!beforeUrl || !afterUrl || beforeUrl === afterUrl) return null;

  const openHalfAtClientX = (clientX) => {
    if (!onImageClick || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    if (rect.width <= 0) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    onImageClick(pct < position ? "before" : "after");
  };

  const handleContainerClick = (e) => {
    if (handleRef.current?.contains(e.target)) return;
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    openHalfAtClientX(e.clientX);
  };

  const startHandleDrag = (clientX) => {
    didDragRef.current = false;
    pointerStartXRef.current = clientX;
    setDragging(true);
    updatePosition(clientX);
  };

  const afterImageStyle = frameWidth
    ? {
        width: frameWidth,
        height: "100%",
        left: frameWidth * (position / 100) * -1,
        ...(afterFilter ? { filter: afterFilter } : {}),
      }
    : {
        width: "100%",
        height: "100%",
        left: 0,
        ...(afterFilter ? { filter: afterFilter } : {}),
      };

  return (
    <div
      ref={containerRef}
      role="presentation"
      className={`relative w-full max-w-[560px] aspect-[16/9] rounded-xl border border-mediumgray overflow-hidden select-none bg-lightgray cursor-pointer ${className}`}
      onClick={handleContainerClick}
    >
      {/* Before (Original/HDR) = full base — left side */}
      <img
        src={beforeUrl}
        alt={beforeLabel}
        className="absolute inset-0 z-0 w-full h-full object-cover pointer-events-none"
        draggable={false}
        loading="lazy"
      />
      {position > 12 && (
        <span className="absolute top-2 left-2 z-[1] bg-black/70 text-white text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded font-mono pointer-events-none">
          {beforeLabel}
        </span>
      )}

      {/* After (AI) = right panel from divider to end */}
      <div
        className="absolute inset-y-0 right-0 z-[1] overflow-hidden pointer-events-none bg-lightgray border-l border-white/80"
        style={{ left: `${position}%` }}
      >
        <img
          src={afterUrl}
          alt={afterLabel}
          className="absolute top-0 max-w-none object-cover"
          style={afterImageStyle}
          draggable={false}
          loading="lazy"
        />
        {position < 88 && (
          <span className="absolute top-2 right-2 z-[1] bg-black/70 text-white text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded font-mono">
            {afterLabel}
          </span>
        )}
      </div>

      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg z-[2] pointer-events-none"
        style={{ left: `${position}%` }}
      >
        <div
          ref={handleRef}
          role="slider"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(position)}
          aria-label={`Compare ${beforeLabel} and ${afterLabel}`}
          tabIndex={0}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white border border-mediumgray shadow-lg flex items-center justify-center cursor-ew-resize pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            startHandleDrag(e.clientX);
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.touches?.[0]) startHandleDrag(e.touches[0].clientX);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") {
              e.preventDefault();
              setPosition((p) => Math.max(0, p - 5));
            } else if (e.key === "ArrowRight") {
              e.preventDefault();
              setPosition((p) => Math.min(100, p + 5));
            }
          }}
        >
          <div className="flex gap-0.5">
            <div className="w-0.5 h-2.5 bg-darkgray" />
            <div className="w-0.5 h-2.5 bg-darkgray" />
          </div>
        </div>
      </div>
    </div>
  );
}
